import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, CheckCircle2, XCircle, Tag, Users, ShoppingBag, Trash2, Eye, Stethoscope, ArrowRight, Settings, QrCode, BookOpen } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import QRCodeDialog from "@/components/QRCodeDialog";

const STATUS_LABELS = {
  pending: { text: "Pendiente", className: "bg-amber-100 text-amber-900" },
  approved: { text: "Aprobado", className: "bg-emerald-100 text-emerald-900" },
  rejected: { text: "Rechazado", className: "bg-rose-100 text-rose-900" },
  sold: { text: "Vendido", className: "bg-slate-200 text-slate-800" },
};

export default function AdminPage() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, sold: 0, users: 0, service_new: 0, service_total: 0 });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get("/admin/listings", { params: { status: tab } }),
        api.get("/admin/stats"),
      ]);
      setItems(list.data || []);
      setStats(st.data || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  const updateStatus = async (id, status) => {
    try {
      await api.post(`/admin/listings/${id}/status`, { status });
      toast.success("Estado actualizado");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteListing = async (id) => {
    if (!confirm("¿Eliminar esta publicación permanentemente?")) return;
    try {
      await api.delete(`/admin/listings/${id}`);
      toast.success("Eliminado");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-blue-700 text-white grid place-items-center shadow">
            <Shield className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-blue-950">Panel de administración</h1>
            <p className="text-slate-600 mt-1 text-base">Aprueba, rechaza o elimina publicaciones para mantener la calidad.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <StatCard label="Pendientes" value={stats.pending} icon={Tag} color="bg-amber-500" testId="stat-pending" />
          <StatCard label="Aprobados" value={stats.approved} icon={CheckCircle2} color="bg-emerald-500" testId="stat-approved" />
          <StatCard label="Vendidos" value={stats.sold} icon={ShoppingBag} color="bg-blue-600" testId="stat-sold" />
          <StatCard label="Rechazados" value={stats.rejected} icon={XCircle} color="bg-rose-500" testId="stat-rejected" />
          <StatCard label="Usuarios" value={stats.users} icon={Users} color="bg-slate-700" testId="stat-users" />
        </div>

        {/* Service requests card */}
        <Link to="/admin/servicios" className="block mb-4" data-testid="admin-services-link">
          <Card className="p-5 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-white/15 grid place-items-center">
                <Stethoscope className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="font-display font-semibold text-lg">Solicitudes de servicio de enfermería</div>
                <div className="text-sm text-blue-100">
                  <span className="font-semibold text-white">{stats.service_new}</span> nuevas · <span className="font-semibold text-white">{stats.service_total}</span> totales
                </div>
              </div>
              <ArrowRight className="w-5 h-5" />
            </div>
          </Card>
        </Link>

        {/* Config card */}
        <Link to="/admin/config" className="block mb-3" data-testid="admin-config-link">
          <Card className="p-5 rounded-2xl border border-slate-200 bg-white hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-slate-100 grid place-items-center">
                <Settings className="w-6 h-6 text-slate-700" />
              </div>
              <div className="flex-1">
                <div className="font-display font-semibold text-base text-slate-900">Configuración y monitoreo</div>
                <div className="text-sm text-slate-500">Tuning en vivo: aprobación automática, límites, salud del sistema</div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
            </div>
          </Card>
        </Link>

        {/* Catalog admin card */}
        <Link to="/admin/catalogo" className="block mb-6" data-testid="admin-catalog-link">
          <Card className="p-5 rounded-2xl border border-slate-200 bg-white hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-blue-100 grid place-items-center">
                <BookOpen className="w-6 h-6 text-blue-700" />
              </div>
              <div className="flex-1">
                <div className="font-display font-semibold text-base text-slate-900">Catálogo y aliases</div>
                <div className="text-sm text-slate-500">Agrega, edita o elimina medicamentos y sus nombres comerciales</div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
            </div>
          </Card>
        </Link>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 p-1 h-auto rounded-xl flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="pending" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Pendientes</TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Aprobados</TabsTrigger>
            <TabsTrigger value="rejected" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Rechazados</TabsTrigger>
            <TabsTrigger value="sold" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Vendidos</TabsTrigger>
            <TabsTrigger value="todos" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Todos</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="text-center text-slate-500 py-10">Cargando…</div>
          ) : items.length === 0 ? (
            <Card className="p-10 rounded-2xl border border-dashed border-slate-300 text-center" data-testid="admin-empty">
              <p className="text-slate-600">Sin publicaciones en esta categoría.</p>
            </Card>
          ) : (
            items.map((it) => (
              <Card key={it.id} className="p-4 rounded-2xl border border-slate-200 bg-white" data-testid={`admin-row-${it.id}`}>
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <img src={it.photo_front} alt={it.medicine_name} className="w-full md:w-24 h-24 object-cover rounded-xl bg-slate-100" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-semibold text-base text-slate-900 truncate">{it.medicine_name}</h3>
                      <Badge className={`${STATUS_LABELS[it.status]?.className} hover:${STATUS_LABELS[it.status]?.className}`}>
                        {STATUS_LABELS[it.status]?.text || it.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {it.category} · {it.total_units} {it.unit_label} · {it.zone} · ${it.base_cost.toFixed(0)} MXN
                    </div>
                    <div className="text-xs text-slate-500">Publica: {it.created_by_name}</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`admin-view-${it.id}`}>
                          <Eye className="w-4 h-4 mr-1" /> Ver
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>{it.medicine_name}</DialogTitle>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-3">
                          {["photo_front","photo_back","photo_expiration","photo_contents"].map((k) => (
                            <img key={k} src={it[k]} alt={k} className="w-full h-40 object-cover rounded-lg bg-slate-100" />
                          ))}
                        </div>
                        <div className="text-sm text-slate-700">
                          <p><strong>Caducidad:</strong> {it.expiration_date || "No especificada"}</p>
                          <p><strong>WhatsApp:</strong> {it.whatsapp}</p>
                          <p><strong>Escaneos QR:</strong> {it.qr_scans || 0}</p>
                          {it.description && <p className="mt-2 whitespace-pre-line">{it.description}</p>}
                        </div>
                      </DialogContent>
                    </Dialog>

                    <QRCodeDialog
                      listingId={it.id}
                      medicineName={it.medicine_name}
                      price={it.base_cost?.toFixed(0)}
                      zone={it.zone}
                      trigger={
                        <Button variant="outline" size="sm" data-testid={`admin-qr-${it.id}`}>
                          <QrCode className="w-4 h-4 mr-1" /> QR
                          {it.qr_scans > 0 && <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-800 px-1 py-0.5 rounded">{it.qr_scans}</span>}
                        </Button>
                      }
                    />

                    {it.status !== "approved" && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateStatus(it.id, "approved")} data-testid={`admin-approve-${it.id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                      </Button>
                    )}
                    {it.status !== "rejected" && (
                      <Button size="sm" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => updateStatus(it.id, "rejected")} data-testid={`admin-reject-${it.id}`}>
                        <XCircle className="w-4 h-4 mr-1" /> Rechazar
                      </Button>
                    )}
                    {it.status === "approved" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(it.id, "sold")} data-testid={`admin-sold-${it.id}`}>
                        <ShoppingBag className="w-4 h-4 mr-1" /> Vendido
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50" onClick={() => deleteListing(it.id)} data-testid={`admin-delete-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testId }) {
  return (
    <Card className="p-4 rounded-2xl border border-slate-200 bg-white" data-testid={testId}>
      <div className={`h-10 w-10 rounded-xl ${color} text-white grid place-items-center shadow-sm`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-3 text-2xl font-semibold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </Card>
  );
}
